import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { store } from '../root/ReduxStore.store';
import { ProductCardVariant } from '../product/product-model';
import { OrderSearchParameter } from '../order';
import { AssignedDivision, NewCustomerPayload, SelectedCfa } from '../customer';
import { RootState } from '../root/root-types';
import { isUndefined } from 'lodash';
import { ConversationQueryType, ConversationThread } from '../conversations';
import { Address } from '../trip/trip-model';

export const COMMON_FEATURE_KEY = 'commonData';

export interface CommonDataState {
  searchKey: string;
  selectedItemIds: string[];
  selectedTeamMemberIds: string[];
  selectedBeatPlanIds: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedItemsMap: Record<any, any>;
  isSimulation: boolean;
  extraDetails: {
    customerName?: string;
    companyName?: string;
    customerId?: string;
    mobile?: string;
    address?: Address;
    orderRefCode?: string;
    variantType?: ProductCardVariant;
    productVariantId?: number;
    poFileId?: string;
    poFileLineId?: string;
    orderLineId?: number;
    routeName?: string;
    routeId?: string;
    routeCustomerGroup?: {
      routeName?: string;
      customerGroupMembersCount?: number;
      routeId?: string;
    };
    priceListId?: string;
    orderId?: string;
    orderNumId?: string;
    teamMemberName?: string;
  };
  sendType: string;
  isSelectedAll: boolean;
  searchParameter?: OrderSearchParameter;
  showSearchWindow?: boolean;
  isOnline?: boolean;
  isDefaultCfas: boolean;
  isDefaultDivisions: boolean;
  divisionsbyCfaId: Record<string, AssignedDivision[]>;
  selectedCfas: SelectedCfa[];
  newCustomerData: NewCustomerPayload;
  recordData: Record<string, string> | undefined;
  queryData: ConversationQueryType | undefined;
  queryThread: ConversationThread | undefined;
}

export const initialCommonDataState: CommonDataState = {
  searchKey: '',
  selectedItemIds: [],
  selectedTeamMemberIds: [],
  selectedBeatPlanIds: [],
  selectedItemsMap: {},
  isSimulation: false,
  extraDetails: {
    customerName: '',
    companyName: '',
    orderRefCode: '',
    mobile: '',
    customerId: '',
    variantType: ProductCardVariant.ORDER_ADD,
  },
  sendType: '',
  isSelectedAll: false,
  searchParameter: {
    parameterId: '',
    value: '',
    updateSearchValue: '',
  } as OrderSearchParameter,
  showSearchWindow: false,
  isOnline: false,
  isDefaultCfas: false,
  isDefaultDivisions: false,
  divisionsbyCfaId: {},
  selectedCfas: [],
  newCustomerData: {
    mobileNumber: '',
    customerName: '',
    firmName: '',
    customerCode: '',
    location: ['', ''],
  },
  recordData: undefined,
  queryData: undefined,
  queryThread: undefined,
};

export const commonDataSlice = createSlice({
  name: COMMON_FEATURE_KEY,
  initialState: initialCommonDataState,
  reducers: {
    updateSearchKey: (state, action: PayloadAction<string>) => {
      state.searchKey = action.payload;
    },
    removeSearchKey: (state) => {
      state.searchKey = '';
    },
    updateSelectedItemIds: (
      state,
      action: PayloadAction<string | string[]>
    ) => {
      const id = action.payload;
      if (id && Array.isArray(id)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.selectedItemIds = id;
      }
      if (id && typeof id === 'string') {
        const index = state.selectedItemIds.indexOf(id);

        if (index > -1) {
          state.selectedItemIds.splice(index, 1);
        } else {
          state.selectedItemIds.push(id);
        }
      }
    },
    updateSelectedItemSingleSelection: (
      state,
      action: PayloadAction<string>
    ) => {
      state.selectedItemIds = [action.payload];
    },
    updateSelectedTeamMemberIds: (
      state,
      action: PayloadAction<string | string[]>
    ) => {
      const id = action.payload;
      if (id && Array.isArray(id)) {
        state.selectedTeamMemberIds = id;
      }
      if (id && typeof id === 'string') {
        const index = state.selectedTeamMemberIds.indexOf(id);

        if (index > -1) {
          state.selectedTeamMemberIds.splice(index, 1);
        } else {
          state.selectedTeamMemberIds.push(id);
        }
      }
    },
    clearSelectedTeamMemberIds: (state) => {
      state.selectedTeamMemberIds = [];
    },
    updateSelectedBeatPlanIds: (
      state,
      action: PayloadAction<string | string[]>
    ) => {
      const id = action.payload;
      if (id && Array.isArray(id)) {
        state.selectedBeatPlanIds = id;
      }
      if (id && typeof id === 'string') {
        const index = state.selectedBeatPlanIds.indexOf(id);

        if (index > -1) {
          state.selectedBeatPlanIds.splice(index, 1);
        } else {
          state.selectedBeatPlanIds.push(id);
        }
      }
    },
    clearSelectedBeatPlanIds: (state) => {
      state.selectedBeatPlanIds = [];
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateSelectedItemsMap: (state, action: PayloadAction<any>) => {
      if (Array.isArray(action.payload)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.selectedItemIds = action.payload
          ?.filter(Boolean)
          ?.map((item: any) => item.id);

        state.selectedItemsMap = action.payload?.filter(Boolean)?.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: any, item: any) => {
            acc[item.id] = item;
            return acc;
          },
          {}
        );
        return;
      }

      const item = action.payload;
      const itemId = item.id;

      const alreadySelected = state.selectedItemIds.includes(itemId);

      if (alreadySelected) {
        state.selectedItemIds = state.selectedItemIds.filter(
          (id) => id !== itemId
        );
        delete state.selectedItemsMap[itemId];
      } else if (itemId) {
        state.selectedItemIds.push(itemId);
        state.selectedItemsMap[itemId] = item;
      }
    },
    clearSelectedItemIds: (state) => {
      state.selectedItemIds = [];
    },
    clearSelectedItemsMap: (state) => {
      state.selectedItemsMap = {};
    },
    updateisSimulation: (state, action: PayloadAction<boolean>) => {
      state.isSimulation = action.payload;
    },
    updateExtraDetails: (
      state,
      action: PayloadAction<CommonDataState['extraDetails']>
    ) => {
      state.extraDetails = { ...state.extraDetails, ...action.payload };
    },
    clearExtraDetails: (state) => {
      state.extraDetails = initialCommonDataState.extraDetails;
    },
    updateSendType: (state, action: PayloadAction<string>) => {
      state.sendType = action.payload;
    },
    updateIsSelectedAll: (state, action: PayloadAction<boolean>) => {
      state.isSelectedAll = action.payload;
    },
    updateSearchParameterInStore: (
      state,
      action: PayloadAction<OrderSearchParameter>
    ) => {
      state.searchParameter = { ...state.searchParameter, ...action.payload };
    },
    updateShowSearchWindow: (state, action: PayloadAction<boolean>) => {
      state.showSearchWindow = action.payload;
    },
    updateIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    updateHasDefaultCfas: (state, action: PayloadAction<boolean>) => {
      state.isDefaultCfas = action.payload;
    },
    updateHasDefaultDivisions: (state, action: PayloadAction<boolean>) => {
      state.isDefaultDivisions = action.payload;
    },
    updateSelectedCfa: (state, action: PayloadAction<SelectedCfa>) => {
      const { id } = action.payload;
      const index = state.selectedCfas.findIndex((cfa) => cfa.id === id);

      if (index !== -1) {
        state.selectedCfas.splice(index, 1);
      } else {
        state.selectedCfas.push(action.payload);
      }
    },
    updateCfaDivisions: (
      state,
      action: PayloadAction<{
        cfaId: string;
        divisions: AssignedDivision[];
      }>
    ) => {
      state.divisionsbyCfaId[action.payload.cfaId] = action.payload.divisions;
    },
    clearCfaDivisions: (state) => {
      state.divisionsbyCfaId = {};
    },
    clearSelectedCfas: (state) => {
      state.selectedCfas = [];
    },
    updateNewCustomerPayload: (
      state,
      action: PayloadAction<NewCustomerPayload | undefined>
    ) => {
      if (!isUndefined(action?.payload)) {
        state.newCustomerData = action.payload;
      } else {
        state.newCustomerData = {
          mobileNumber: '',
          customerName: '',
          firmName: '',
          customerCode: '',
          location: ['', ''],
        };
      }
    },
    updateRecordData: (
      state,
      action: PayloadAction<Record<string, string> | undefined>
    ) => {
      if (isUndefined(action?.payload)) {
        state.recordData = undefined;
      } else {
        state.recordData = action?.payload;
      }
    },
    updateQueryData: (
      state,
      action: PayloadAction<ConversationQueryType | undefined>
    ) => {
      if (isUndefined(action?.payload)) {
        state.queryData = undefined;
      } else {
        state.queryData = action?.payload;
      }
    },
    updateQueryThread: (
      state,
      action: PayloadAction<ConversationThread | undefined>
    ) => {
      if (isUndefined(action?.payload)) {
        state.queryThread = undefined;
      } else {
        state.queryThread = action?.payload;
      }
    },
  },
});

export const commonDataReducer = commonDataSlice.reducer;

export const {
  updateSearchKey,
  removeSearchKey,
  updateSelectedItemIds,
  updateSelectedItemsMap,
  clearSelectedItemIds,
  updateSelectedTeamMemberIds,
  clearSelectedTeamMemberIds,
  updateSelectedBeatPlanIds,
  clearSelectedBeatPlanIds,
  clearSelectedItemsMap,
  updateisSimulation,
  updateExtraDetails,
  clearExtraDetails,
  updateSendType,
  updateIsSelectedAll,
  updateSearchParameterInStore,
  updateShowSearchWindow,
  updateIsOnline,
  updateHasDefaultCfas,
  updateHasDefaultDivisions,
  updateCfaDivisions,
  clearCfaDivisions,
  updateSelectedCfa,
  clearSelectedCfas,
  updateNewCustomerPayload,
  updateSelectedItemSingleSelection,
  updateRecordData,
  updateQueryData,
  updateQueryThread,
} = commonDataSlice.actions;

export const getCommonDataSliceState = (
  rootState: ReturnType<typeof store.getState>
): CommonDataState => rootState[COMMON_FEATURE_KEY];

export const getSearchKey = createSelector(
  getCommonDataSliceState,
  (state) => state.searchKey
);

export const getSelectedItemIds = createSelector(
  getCommonDataSliceState,
  (state) => state.selectedItemIds
);

export const getSelectedTeamMemberIds = createSelector(
  getCommonDataSliceState,
  (state) => state.selectedTeamMemberIds
);

export const getSelectedBeatPlanIds = createSelector(
  getCommonDataSliceState,
  (state) => state.selectedBeatPlanIds
);

export const getSelectedItems = createSelector(
  getCommonDataSliceState,
  (state) => Object.values(state.selectedItemsMap)
);

export const getIsSimulation = createSelector(
  getCommonDataSliceState,
  (state) => state.isSimulation
);

export const getExtraDetails = createSelector(
  getCommonDataSliceState,
  (state) => state.extraDetails
);

export const getSendType = createSelector(
  getCommonDataSliceState,
  (state) => state.sendType
);

export const getIsSelectedAll = createSelector(
  getCommonDataSliceState,
  (state) => state.isSelectedAll
);

export const getSearchParameter = createSelector(
  getCommonDataSliceState,
  (state) => state.searchParameter
);

export const getShowSearchWindow = createSelector(
  getCommonDataSliceState,
  (state) => state.showSearchWindow
);

export const getIsOnline = createSelector(
  getCommonDataSliceState,
  (state) => state.isOnline
);

export const getHasDefaultCfas = createSelector(
  getCommonDataSliceState,
  (state) => state.isDefaultCfas
);

export const getHasDefaultDivision = createSelector(
  getCommonDataSliceState,
  (state) => state.isDefaultDivisions
);

export const getSelectedCfas = createSelector(
  getCommonDataSliceState,
  (state) => state.selectedCfas
);

export const getDivisionsByCfaId = createSelector(
  [getCommonDataSliceState, (_: RootState, cfaId?: string | null) => cfaId],
  (state, cfaId) => (cfaId ? (state.divisionsbyCfaId[cfaId] ?? []) : [])
);

export const getNewCustomerPayload = createSelector(
  getCommonDataSliceState,
  (state) => state.newCustomerData
);

export const getCfaPayload = createSelector(
  [getSelectedCfas, getCommonDataSliceState],
  (selectedCfas, commonState) =>
    selectedCfas
      .map((cfa) => {
        const divisions = commonState.divisionsbyCfaId[cfa.id];
        if (!divisions || divisions.length === 0) return null;

        return {
          cfaCode: cfa.cfaCode,
          divisionCodes: divisions.map((division) => ({
            code: division.code,
          })),
        };
      })
      .filter(Boolean)
);

export const getRecordData = createSelector(
  getCommonDataSliceState,
  (state) => state.recordData
);

export const getQueryData = createSelector(
  getCommonDataSliceState,
  (state) => state.queryData
);

export const getQueryThread = createSelector(
  getCommonDataSliceState,
  (state) => state.queryThread
);
